import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Palette, Sparkles, Gamepad2, ChevronRight, Check,
  MessageCircle, Phone, ArrowRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ModdingPage = () => {
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [loading, setLoading] = useState(true);
  
  const [formData, setFormData] = useState({
    device_type: '',
    device_model: '',
    modding_type: '',
    color_preference: '',
    design_notes: '',
    contact_email: '',
    contact_phone: ''
  });

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const res = await fetch(`${API_URL}/api/modding/services`);
      if (res.ok) {
        const data = await res.json();
        setServices(data.services || []);
      }
    } catch (error) {
      console.error('Error fetching services:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const res = await fetch(`${API_URL}/api/modding/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success('Solicitud enviada. Te contactaremos en 24-48h');
        setShowRequestForm(false);
        setFormData({
          device_type: '',
          device_model: '',
          modding_type: '',
          color_preference: '',
          design_notes: '',
          contact_email: '',
          contact_phone: ''
        });
      } else {
        toast.error('Error al enviar solicitud');
      }
    } catch (error) {
      toast.error('Error de conexión');
    }
  };

  const selectService = (service) => {
    setSelectedService(service);
    setFormData({
      ...formData,
      device_type: service.device.includes('Joy-Con') || service.device.includes('Switch') ? 'console' : 
                   service.device.includes('DualSense') ? 'controller' : 'console',
      device_model: service.device,
      modding_type: service.id
    });
    setShowRequestForm(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-900 via-gray-900 to-black text-white" data-testid="modding-page">
      {/* Hero */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-800/20 to-pink-800/20" />
        <div className="max-w-7xl mx-auto px-4 py-16 relative">
          <div className="flex items-center gap-2 text-sm text-purple-300 mb-4">
            <Link to="/" className="hover:text-white">Inicio</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-white">Modding Legal</span>
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-4 flex items-center gap-4">
              <Sparkles className="w-12 h-12 text-purple-400" />
              Modding Legal
            </h1>
            <p className="text-xl text-gray-300 mb-6">
              Personaliza tu consola y mandos con carcasas, LEDs y botones premium. 
              Sin modificaciones de software, 100% legal.
            </p>
            
            <div className="flex flex-wrap gap-4 mb-8">
              <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full">
                <Check className="w-4 h-4 text-green-400" />
                <span className="text-sm">Sin riesgo de ban</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full">
                <Check className="w-4 h-4 text-green-400" />
                <span className="text-sm">Garantía mantenida</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full">
                <Check className="w-4 h-4 text-green-400" />
                <span className="text-sm">Instalación profesional</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Services Grid */}
      <div className="max-w-7xl mx-auto px-4 py-16">
        <h2 className="text-2xl font-bold mb-8">Servicios de Personalización</h2>
        
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-400"></div>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, i) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-6 hover:border-purple-500/50 transition-all group cursor-pointer"
                onClick={() => selectService(service)}
                data-testid={`modding-service-${service.id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center">
                    <Palette className="w-6 h-6 text-purple-400" />
                  </div>
                  <span className="text-purple-400 font-bold">{service.price_range}</span>
                </div>
                
                <h3 className="text-lg font-semibold mb-1 group-hover:text-purple-400 transition-colors">
                  {service.name}
                </h3>
                <p className="text-sm text-gray-400 mb-3">{service.device}</p>
                <p className="text-gray-300 text-sm mb-4">{service.description}</p>
                
                {/* Options */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {service.options?.slice(0, 3).map((opt, j) => (
                    <span key={j} className="text-xs bg-white/10 px-2 py-1 rounded">
                      {opt}
                    </span>
                  ))}
                  {service.options?.length > 3 && (
                    <span className="text-xs text-purple-400">+{service.options.length - 3} más</span>
                  )}
                </div>
                
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Tiempo: {service.time}</span>
                  <ArrowRight className="w-5 h-5 text-purple-400 group-hover:translate-x-1 transition-transform" />
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Request Form Modal */}
      {showRequestForm && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-gray-900 rounded-2xl border border-white/10 p-6 max-w-md w-full max-h-[90vh] overflow-y-auto"
          >
            <h2 className="text-xl font-bold mb-2">Solicitar Modding</h2>
            {selectedService && (
              <p className="text-purple-400 text-sm mb-4">{selectedService.name}</p>
            )}
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-sm text-gray-400">Modelo de dispositivo *</label>
                <Input
                  required
                  value={formData.device_model}
                  onChange={(e) => setFormData({...formData, device_model: e.target.value})}
                  className="bg-white/10 border-white/20"
                  placeholder="Nintendo Switch, DualSense..."
                />
              </div>
              
              <div>
                <label className="text-sm text-gray-400">Preferencia de color</label>
                <Input
                  value={formData.color_preference}
                  onChange={(e) => setFormData({...formData, color_preference: e.target.value})}
                  className="bg-white/10 border-white/20"
                  placeholder="Atomic Purple, Negro mate..."
                />
              </div>
              
              <div>
                <label className="text-sm text-gray-400">Notas adicionales</label>
                <textarea
                  value={formData.design_notes}
                  onChange={(e) => setFormData({...formData, design_notes: e.target.value})}
                  className="w-full bg-white/10 border border-white/20 rounded-lg p-3 text-white"
                  rows={3}
                  placeholder="Describe tu diseño ideal..."
                />
              </div>
              
              <div>
                <label className="text-sm text-gray-400">Email *</label>
                <Input
                  type="email"
                  required
                  value={formData.contact_email}
                  onChange={(e) => setFormData({...formData, contact_email: e.target.value})}
                  className="bg-white/10 border-white/20"
                />
              </div>
              
              <div>
                <label className="text-sm text-gray-400">Teléfono *</label>
                <Input
                  type="tel"
                  required
                  value={formData.contact_phone}
                  onChange={(e) => setFormData({...formData, contact_phone: e.target.value})}
                  className="bg-white/10 border-white/20"
                />
              </div>
              
              <div className="flex gap-3 pt-4">
                <Button type="submit" className="flex-1 bg-purple-600 hover:bg-purple-700">
                  Enviar Solicitud
                </Button>
                <Button 
                  type="button" 
                  variant="outline"
                  onClick={() => setShowRequestForm(false)}
                  className="border-white/20 text-white hover:bg-white/10"
                >
                  Cancelar
                </Button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* Contact Section */}
      <div className="max-w-7xl mx-auto px-4 py-16">
        <div className="bg-gradient-to-r from-purple-600/20 to-pink-600/20 rounded-2xl p-8 text-center border border-white/10">
          <h2 className="text-2xl font-bold mb-4">¿Tienes una idea única?</h2>
          <p className="text-gray-300 mb-6">
            Contáctanos para proyectos personalizados. Hacemos realidad tu diseño.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="https://wa.me/34624473293">
              <Button className="bg-green-600 hover:bg-green-700">
                <MessageCircle className="w-4 h-4 mr-2" />
                WhatsApp
              </Button>
            </a>
            <a href="tel:+34624473293">
              <Button variant="outline" className="border-white/20 text-white hover:bg-white/10">
                <Phone className="w-4 h-4 mr-2" />
                624 47 32 93
              </Button>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModdingPage;
